/**
 * What is ALREADY wrong with an asset, recorded before it goes out.
 *
 * The question this exists to answer is asked months later, at repair review: an
 * employee reports that the F5 key is dead, and IT has to decide whether that happened
 * on their watch or was true the day they received it. With no record the answer is
 * whoever sounds more certain, which is how a repair turns into an argument and how a
 * recovery charge gets levied against the wrong person.
 *
 * `Assignment.condition` already grades a machine NEW/GOOD/FAIR/POOR, but a grade
 * cannot say WHICH key, and "FAIR" six months later means nothing to anybody. These
 * are the specifics that a grade cannot carry.
 *
 * Like COMMON_ACCESSORIES, this list is a SUGGESTION and not an enum. Faults are
 * stored as free text, so a company that needs "Hinge cracked" or "Stylus lost" adds
 * one by typing it - no migration, no deploy. These are only what the checklist offers
 * before anyone types anything.
 */
export const COMMON_FAULTS = [
  'Screen damage',
  'Dead keyboard keys',
  'Battery weak',
  'Runs slow',
  'Scratches or dents',
  'Touchpad faulty',
  'Port not working',
  'Speaker or mic faulty',
  'Camera faulty',
  'Fan noisy',
  'Overheats',
  'Hinge loose',
] as const;

/** Free text, so this bounds how long one entry may be rather than which it is. */
export const MAX_FAULT_LENGTH = 80;
export const MAX_FAULTS_PER_ASSET = 20;

/**
 * Normalise for comparison, so "Battery weak" ticked at registration matches
 * "battery weak " typed at handover.
 *
 * The same reasoning as accessoryKey: without it the two spellings are two different
 * faults, and "was this already broken" gets the wrong answer because somebody used a
 * capital letter.
 */
export function faultKey(name: string): string {
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Trimmed, de-duplicated, order preserved. What gets stored. */
export function normalizeFaults(names: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of names) {
    const name = raw.trim();
    if (name === '') continue;
    const key = faultKey(name);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name.slice(0, MAX_FAULT_LENGTH));
  }
  return out.slice(0, MAX_FAULTS_PER_ASSET);
}

/**
 * Faults on the asset now that were NOT on it when this holder received it.
 *
 * The one calculation review actually wants: everything here appeared while the asset
 * was out with them, and everything omitted was theirs to inherit. Compared on the
 * normalised key, and reported in the CURRENT spelling because that is the one the
 * person reading it just typed.
 */
export function faultsSinceHandover(
  atHandover: readonly string[],
  now: readonly string[],
): string[] {
  const known = new Set(atHandover.map(faultKey));
  return now.filter((name) => !known.has(faultKey(name)));
}

/**
 * How to read a condition record, in the only three states it actually has.
 *
 * The trap this avoids: an empty fault list means either "someone checked it and it
 * was clean" or "nobody looked", and those are opposite answers. Collapsing them is
 * how a screen ends up asserting "no known faults" over a check that was never made -
 * which is worth less than nothing at review, because it reads as evidence.
 *
 * So the timestamp is the discriminator, not the emptiness of the list - the same
 * reason `lastVerifiedAt` is nullable rather than backfilled to a guess.
 *
 * 'unchecked' is MUTED wherever it is shown, never red. Most furniture will never
 * have a condition record and flagging every chair as a problem is what trains people
 * to ignore the colour.
 */
export function conditionVerdict(record: {
  checkedAt: Date | string | null | undefined;
  faults: readonly string[];
}): 'unchecked' | 'clean' | 'faults' {
  if (record.checkedAt == null) return 'unchecked';
  return record.faults.length > 0 ? 'faults' : 'clean';
}

export const CONDITION_VERDICT_LABELS: Record<
  ReturnType<typeof conditionVerdict>,
  string
> = {
  unchecked: 'Condition not recorded',
  clean: 'Checked, nothing wrong',
  faults: 'Known faults',
};

/**
 * Whether two fault lists say the same thing.
 *
 * Compared as SETS on the normalised key, so neither a reordering nor a difference in
 * capitals counts as a change. That matters because the caller uses this to decide
 * whether to re-stamp `conditionCheckedAt`: the edit form posts the whole record on
 * every save, so a rename or a status change arrives carrying the fault list it was
 * seeded with, and treating that as an edit would have every save assert that somebody
 * had just inspected the machine.
 */
export function sameFaults(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a.map(faultKey));
  // Sizes can differ from the array lengths if a list holds duplicates that only
  // collide once normalised - comparing the sets catches that too.
  const right = new Set(b.map(faultKey));
  if (left.size !== right.size) return false;
  for (const key of left) {
    if (!right.has(key)) return false;
  }
  return true;
}
